import { BrowserUtils, Configuration, InteractionRequiredAuthError, PublicClientApplication, SilentRequest } from '@azure/msal-browser';

import { VersionInfo } from '../../../Config';
import { isBedBoardIntakeForm } from '../helpers';

// For a full list of msal.js configuration parameters visit
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md

const getSystemConfig = () => {
    /*
    return {
        loggerOptions: {
            loggerCallback: (level: LogLevel, message: string, containsPii: boolean): void => {
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        //console.info(message);
                        return;
                    case LogLevel.Verbose:
                        //console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                }
            },

            piiLoggingEnabled: true,
        },
    };
    */

    return null;
};

export const msalConfig: Configuration = {
    auth: {
        authority: process.env.REACT_APP_MS_AUTHORITY,
        clientId: process.env.REACT_APP_MS_CLIENTID,
        redirectUri: window.location.origin,
        onRedirectNavigate: () => !BrowserUtils.isInIframe(),
    },

    cache: {
        cacheLocation: 'localStorage', // This configures where your cache will be stored
    },

    system: {
        ...getSystemConfig(),
        allowPlatformBroker: false, // Disables WAM Broker
    },

    telemetry: {
        application: {
            appName: 'MedOne Post-Acute',
            appVersion: VersionInfo.AppVersion,
        },
    },
};

// Coordinates and required scopes for your web api
export const apiConfig = {
    // Enter the API scopes as declared in the app registration 'Expose an Api' blade in the form of 'api://{client_id}/.default'
    resourceScope: process.env.REACT_APP_MS_SCOPES,
};

/**
 * Scopes you enter here will be consented once you authenticate. For a full list of available authentication parameters,
 * visit https://azuread.github.io/microsoft-authentication-library-for-js/docs/msal/modules/_authenticationparameters_.html
 */
export const loginRequest = {
    scopes: ['openid', 'profile', 'offline_access'],
    redirectUri: `${window.location.origin}`,
};

// Add here scopes for access token to be used at the API endpoints.
export const tokenRequest = {
    scopes: [apiConfig.resourceScope],
    redirectUri: `${window.location.origin}`,
};

// Add here scopes for silent token request
export const silentRequest = {
    scopes: ['openid', 'profile', apiConfig.resourceScope],
    redirectUri: `${window.location.origin}`,
} as SilentRequest;

export const graphConfig = {
    graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
};

export const msalInstance = new PublicClientApplication(msalConfig);

let forceRefresh = true;

export type BeforeAuthRedirectHandler = () => void | Promise<void>;

const beforeAuthRedirectHandlers = new Set<BeforeAuthRedirectHandler>();

/**
 * Registers work to run right before the app leaves for the sign-in page, for example keeping a
 * local copy of a note the user is still typing. Returns the function that unregisters the handler.
 */
export const onBeforeAuthRedirect = (handler: BeforeAuthRedirectHandler): (() => void) => {
    beforeAuthRedirectHandlers.add(handler);

    return () => {
        beforeAuthRedirectHandlers.delete(handler);
    };
};

// The redirect must not hang on a slow handler: whatever has not finished by then is skipped.
const BEFORE_REDIRECT_TIMEOUT_MS = 1500;

const runBeforeAuthRedirectHandlers = async (): Promise<void> => {
    if (beforeAuthRedirectHandlers.size === 0) {
        return;
    }

    const work = Promise.allSettled(Array.from(beforeAuthRedirectHandlers).map((handler) => Promise.resolve().then(handler)));

    await Promise.race([work, new Promise<void>((resolve) => setTimeout(resolve, BEFORE_REDIRECT_TIMEOUT_MS))]);
};

// Error codes for which MSAL cannot renew the token without the user signing in again.
// Anything else (network, iframe timeout, throttling) is transient and must not send the user to the sign-in page.
const INTERACTION_ERROR_CODES = new Set(['interaction_required', 'login_required', 'consent_required', 'invalid_grant', 'no_tokens_found', 'no_account_error']);

export const requiresInteraction = (error: unknown): boolean => {
    if (error instanceof InteractionRequiredAuthError) {
        return true;
    }

    const errorCode = (error as { errorCode?: string })?.errorCode ?? '';

    return INTERACTION_ERROR_CODES.has(errorCode);
};

const RETRY_DELAY_MS = 1000;
const MAX_CONSECUTIVE_SILENT_FAILURES = 3;

let isRedirecting = false;
let consecutiveSilentFailures = 0;

const describe = (error: unknown): string => {
    const e = error as { errorCode?: string; message?: string };

    return `${e?.errorCode ?? 'unknown'}: ${e?.message ?? String(error)}`;
};

const redirectToLogin = async (reason: string): Promise<string> => {
    // Several requests can fail at once; only the first one may start the redirect.
    if (isRedirecting) {
        return '';
    }

    isRedirecting = true;
    console.warn(`Redirecting to sign-in: ${reason}`);

    try {
        await runBeforeAuthRedirectHandlers();
    } catch (handlerError) {
        console.error('A before-redirect handler failed:', handlerError);
    }

    try {
        await msalInstance.loginRedirect(loginRequest);
    } catch (redirectError) {
        isRedirecting = false;

        throw redirectError;
    }

    return '';
};

/**
 * Returns an access token for the API. Silent renewal is retried once on transient failures and only
 * a failure that genuinely needs the user (or three transient failures in a row) redirects to sign-in;
 * every other failure is thrown to the caller so the current page, and whatever is typed on it, stays.
 */
export const acquireAccessToken = async (refresh = false): Promise<string> => {
    // Don't try to get token during public intake form requests
    if (isBedBoardIntakeForm()) {
        return '';
    }

    if (refresh) {
        forceRefresh = true;
    }

    // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
    const activeAccount = msalInstance.getActiveAccount();
    const accounts = msalInstance.getAllAccounts();

    if (!activeAccount && accounts.length === 0) {
        return redirectToLogin('no signed-in account');
    }

    const request = {
        ...silentRequest,
        account: activeAccount || accounts[0],
        forceRefresh: forceRefresh,
    } as SilentRequest;

    try {
        forceRefresh = false;

        const authResult = await msalInstance.acquireTokenSilent(request);

        consecutiveSilentFailures = 0;

        return authResult.accessToken;
    } catch (error: unknown) {
        if (requiresInteraction(error)) {
            return redirectToLogin(`silent renewal needs the user to sign in (${describe(error)})`);
        }

        console.warn(`Silent token renewal failed, retrying once (${describe(error)})`);

        await new Promise<void>((resolve) => setTimeout(resolve, RETRY_DELAY_MS));

        try {
            const retryResult = await msalInstance.acquireTokenSilent({ ...request, forceRefresh: true });

            consecutiveSilentFailures = 0;

            return retryResult.accessToken;
        } catch (retryError: unknown) {
            if (requiresInteraction(retryError)) {
                return redirectToLogin(`silent renewal needs the user to sign in (${describe(retryError)})`);
            }

            consecutiveSilentFailures++;

            if (consecutiveSilentFailures >= MAX_CONSECUTIVE_SILENT_FAILURES) {
                consecutiveSilentFailures = 0;

                return redirectToLogin(`silent renewal failed ${MAX_CONSECUTIVE_SILENT_FAILURES} times in a row (${describe(retryError)})`);
            }

            // Let the request fail; the page and the user's typing stay where they are.
            throw retryError;
        }
    }
};
